import React, { useState, useRef, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { useTranslation } from 'react-i18next';
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';
import PageHeader from '../../components/Shared/PageHeader';
import SharedButton from '../../components/Shared/SharedButton';
import { fetchData } from '../../helpers/fetchData';

const DailyInvoiceReport = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useSelector(state => state.auth);
    const buisnessInfo = auth?.user?.Business;

    const printRef = useRef();
    const iframeRef = useRef(null);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const [logoBase64, setLogoBase64] = useState(null);

    const parseInitialDate = (dateValue, defaultDate) => {
        if (!dateValue) return defaultDate;
        const parsed = new Date(dateValue);
        return isNaN(parsed.getTime()) ? defaultDate : parsed;
    };

    const initialStartDate = parseInitialDate(
        location.state?.start_date,
        moment().subtract(1, 'day').toDate()
    );

    const initialEndDate = parseInitialDate(
        location.state?.end_date,
        moment().toDate()
    );

    const [filters, setFilters] = useState({
        start_date: initialStartDate,
        end_date: initialEndDate,
        department: location.state?.department_id || '',
    });

    useEffect(() => {
        if (buisnessInfo?.logo) {
            if (buisnessInfo.logo.startsWith('http')) {
                setLogoBase64(buisnessInfo.logo);
            } else {
                setLogoBase64(`data:image/png;base64,${buisnessInfo.logo}`);
            }
        } else {
            setLogoBase64(null);
        }
    }, [buisnessInfo?.logo]);

    useEffect(() => {
        fetchDepartments();
    }, []);

    useEffect(() => {
        if (filters?.department) {
            fetchReport();
        }
    }, [filters?.department]);

    const fetchDepartments = async () => {
        setDepartmentsLoading(true);
        try {
            const response = await fetchData('POST', 'his/departments/getList');
            if (response?.data) {
                const options = response.data.map(dept => ({
                    label: i18n.language === 'ar' ? dept.name_ar : dept.name_en,
                    value: dept.id,
                    ...dept
                }));
                setDepartments(options);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        } finally {
            setDepartmentsLoading(false);
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const payload = {
                start_date: moment(filters.start_date).format('YYYY-MM-DD'),
                end_date: moment(filters.end_date).format('YYYY-MM-DD'),
            };

            if (filters.department) {
                payload.department = filters.department;
            }

            const response = await fetchData('POST', 'his/groupReport', payload);
            setData(response);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
        const printContents = printRef.current.innerHTML;
        const iframe = iframeRef.current;
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write('<html><head><title>' + t('Transaction Report') + '</title>');
        doc.write('<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css">');
        doc.write('<style>');
        doc.write(`
            @media print {
                @page { margin: 20px 0 20px 0; }
                body { margin: 1cm; direction: ${direction}; }
                header, footer { display: none; }
                table { direction: ${direction}; border-collapse: collapse; width: 100%; }
                th, td { border: 1px solid #999; padding: 4px 8px; font-size: 10px; }
            }
        `);
        doc.write('</style></head><body>');
        doc.write(printContents);
        doc.write('</body></html>');
        doc.close();

        iframe.onload = () => {
            iframe.contentWindow.print();
        };
    };

    const grandTotals = data?.reports?.reduce((acc, report) => {
        const groupKey = report.invoice_group?.toLowerCase() || '';
        const isAdvance = groupKey.includes('advance');
        const isCash = (groupKey.includes('cash') || groupKey.includes('sales')) && !isAdvance;

        const subtotal = report.invoices?.reduce((s, inv) => {
            let charge = 0, vat = 0, total = 0;

            if (isAdvance) {
                total = parseFloat(inv.total_amount) || 0;
            } else if (isCash) {
                charge = parseFloat(inv.patient_share) || 0;
                vat = parseFloat(inv.patient_vat) || 0;
                total = parseFloat(inv.net_patient_payable_amount) || 0;
            } else {
                charge = parseFloat(inv.company_share) || 0;
                vat = parseFloat(inv.company_vat || inv.vat) || 0;
                total = parseFloat(inv.net_company_payable_amount) || 0;
            }

            return {
                charge: s.charge + charge,
                vat: s.vat + vat,
                total: s.total + total,
            };
        }, { charge: 0, vat: 0, total: 0 }) || { charge: 0, vat: 0, total: 0 };

        return {
            total_charge: acc.total_charge + subtotal.charge,
            total_vat: acc.total_vat + subtotal.vat,
            total_payment: acc.total_payment + subtotal.total,
        };
    }, { total_charge: 0, total_vat: 0, total_payment: 0 }) || {
        total_charge: 0,
        total_vat: 0,
        total_payment: 0,
    };

    const paymentGrandTotals = data?.reports?.reduce((acc, report) => ({
        bank: acc.bank + (parseFloat(report.bank_total) || 0),
        card: acc.card + (parseFloat(report.card_total) || 0),
        cash: acc.cash + (parseFloat(report.cash_total) || 0),
        deposit: acc.deposit + (parseFloat(report.deposit_total) || 0),
    }), { bank: 0, card: 0, cash: 0, deposit: 0 }) || { bank: 0, card: 0, cash: 0, deposit: 0 };

    const cardTypeTotals = data?.reports?.reduce((acc, report) => {
        const cardTypes = report?.card_types || [];
        cardTypes?.forEach(cardType => {
            const type = cardType?.k || 'other';
            const amount = parseFloat(cardType?.v) || 0;
            acc[type] = (acc[type] || 0) + amount;
        });
        return acc;
    }, {}) || {};

    const payersRevenueTotals = data?.reports?.reduce((acc, report) => {
        const payersRevenue = report?.payers_revenue || [];
        payersRevenue?.forEach(payer => {
            const payerName = payer?.payer_name || 'Unknown';
            const revenue = parseFloat(payer?.revenue) || 0;
            acc[payerName] = (acc[payerName] || 0) + revenue;
        });
        return acc;
    }, {}) || {};

    const totalPaymentReceived = paymentGrandTotals.bank + paymentGrandTotals.card + paymentGrandTotals.cash + paymentGrandTotals.deposit;

    const selectedDepartment = departments.find(d => d.value === filters.department);

    return (
        <div className="main-page-container !overflow-y-auto scrollbar">
            <div className="flex items-center justify-between mb-4">
                <PageHeader
                    title={t('Daily Invoice Report')}
                    iconSize={20}
                    Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
                    onIconClick={() => navigate(-1)}
                />
            </div>

            <div className="bg-white p-4 rounded-lg mb-4 flex items-end gap-4">
                <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('StartDate')}
                    </label>
                    <Calendar
                        value={filters.start_date}
                        onChange={(e) => setFilters({ ...filters, start_date: e.value })}
                        dateFormat="dd-mm-yy"
                        className="w-full"
                        inputClassName="w-full p-2 border h-[35px] rounded-md"
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('EndDate')}
                    </label>
                    <Calendar
                        value={filters.end_date}
                        onChange={(e) => setFilters({ ...filters, end_date: e.value })}
                        dateFormat="dd-mm-yy"
                        className="w-full"
                        inputClassName="w-full p-2 border h-[35px] rounded-md"
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('Department')}
                    </label>
                    <select
                        className="w-full p-2 border h-[35px] rounded-md"
                        value={filters.department}
                        onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                        disabled={departmentsLoading}
                    >
                        <option value="">{t('All Departments')}</option>
                        {departments.map((dept) => (
                            <option key={dept.value} value={dept.value}>
                                {dept.label}
                            </option>
                        ))}
                    </select>
                </div>
                <SharedButton
                    label={t('Generate Report')}
                    onClick={fetchReport}
                    icon="pi pi-file"
                    loading={loading}
                    className="h-[38px]"
                />
                {data && (
                    <SharedButton
                        label={t('Print')}
                        icon="pi pi-print"
                        onClick={handlePrint}
                        loading={loading}
                        className="h-[38px] bg-green-600 border-green-600 hover:!text-green-600 hover:!border-green-600"
                    />
                )}
            </div>

            {loading && (
                <div className="bg-white p-6 rounded-lg shadow-sm">
                    <div className="mb-6">
                        <Skeleton height={80} className="mb-4" />
                    </div>

                    <div className="mb-6">
                        <Skeleton height={30} width={200} className="mb-3" />
                        <Skeleton height={50} className="mb-2" />
                        <Skeleton height={40} count={5} />
                    </div>

                    <div className="mb-6">
                        <Skeleton height={30} width={200} className="mb-3" />
                        <Skeleton height={50} className="mb-2" />
                        <Skeleton height={40} count={5} />
                    </div>

                    <div className="mt-8">
                        <Skeleton height={30} width={200} className="mb-3" />
                        <div className="max-w-md">
                            <Skeleton height={50} className="mb-2" />
                            <Skeleton height={40} count={4} />
                        </div>
                    </div>
                </div>
            )}
            {!loading && data?.reports && (
                <div ref={printRef} className="bg-white p-6">
                    <div className="mb-4">
                        <div className="relative flex items-center justify-center min-h-[80px]">
                            <div className="absolute left-0 flex items-center">
                                {logoBase64 ? (
                                    <img
                                        src={logoBase64}
                                        alt="Logo"
                                        className="h-20 w-auto object-contain"
                                    />
                                ) : (
                                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                                        <span className="text-2xl font-bold text-gray-600">
                                            {buisnessInfo?.name_en?.charAt(0) || 'I'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="text-center">
                                <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                                    {buisnessInfo?.name_en || 'Test'}
                                </h1>
                                <p className="text-lg font-bold text-gray-700 mt-1">
                                    {buisnessInfo?.name_ar || 'Test'}
                                </p>
                            </div>

                            <div className="absolute right-0 text-right text-xs text-gray-500">
                                <p>{moment().format('DD-MM-YYYY')}</p>
                                <p>{moment().format('HH:mm')}</p>
                            </div>
                        </div>
                    </div>

                    {data.reports.map((report, index) => {
                        const invoices = report.invoices || [];
                        const groupKey = report.invoice_group?.toLowerCase() || '';
                        const isAdvance = groupKey.includes('advance');
                        const isCash = (groupKey.includes('cash') || groupKey.includes('sales')) && !isAdvance;

                        const subtotal = invoices.reduce((acc, inv) => {
                            let charge = 0, vat = 0, total = 0;

                            if (isAdvance) {
                                total = parseFloat(inv.total_amount) || 0;
                            } else if (isCash) {
                                charge = parseFloat(inv.patient_share) || 0;
                                vat = parseFloat(inv.patient_vat) || 0;
                                total = parseFloat(inv.net_patient_payable_amount) || 0;
                            } else {
                                charge = parseFloat(inv.company_share) || 0;
                                vat = parseFloat(inv.company_vat || inv.vat) || 0;
                                total = parseFloat(inv.net_company_payable_amount) || 0;
                            }

                            return {
                                charge: acc.charge + charge,
                                vat: acc.vat + vat,
                                total: acc.total + total,
                            };
                        }, { charge: 0, vat: 0, total: 0 });

                        return (
                            <div key={index} className={index > 0 ? 'mt-6' : ''}>
                                <div className="mb-2">
                                    <h4 className="text-sm font-bold text-gray-800">
                                        {t('Transaction Type')}: {report.invoice_group || 'Other'}
                                    </h4>
                                </div>

                                <table className="w-full border-collapse border border-gray-400 text-xs">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="border border-gray-400 px-2 py-1 text-left font-semibold">{t('Trans.No')}</th>
                                            <th className="border border-gray-400 px-2 py-1 text-left font-semibold">{t('Date')}</th>
                                            <th className="border border-gray-400 px-2 py-1 text-left font-semibold">{t('Patient Name')}</th>
                                            {
                                                report.invoice_group !== 'Cash' && <th className="border border-gray-400 px-2 py-1 text-left font-semibold">{t('Company Name')}</th>
                                            }
                                            <th className="border border-gray-400 px-2 py-1 text-right font-semibold">{t('Charge')}</th>
                                            <th className="border border-gray-400 px-2 py-1 text-right font-semibold">{t('VAT')}</th>
                                            <th className="border border-gray-400 px-2 py-1 text-right font-semibold">{t('Payment')}</th>
                                            <th className="border border-gray-400 px-2 py-1 text-right font-semibold">{t('Rem')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((inv, idx) => {
                                            let charge = 0, vat = 0, total = 0;
                                            let transNo = inv.invoice_number || '-';
                                            let date = inv.invoice_date || inv.created_at || '-';
                                            let patientName = inv.patient?.full_name || inv.patient_name || '-';
                                            let companyName = inv.company?.name || (isAdvance ? '-' : 'Cash Customer');

                                            if (isAdvance) {
                                                total = parseFloat(inv.total_amount) || 0;
                                            } else if (isCash) {
                                                charge = parseFloat(inv.patient_share) || 0;
                                                vat = parseFloat(inv.patient_vat) || 0;
                                                total = parseFloat(inv.net_patient_payable_amount) || 0;
                                            } else {
                                                charge = parseFloat(inv.company_share) || 0;
                                                vat = parseFloat(inv.company_vat || inv.vat) || 0;
                                                total = parseFloat(inv.net_company_payable_amount) || 0;
                                            }

                                            return (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="border border-gray-400 px-2 py-1">{transNo}</td>
                                                    <td className="border border-gray-400 px-2 py-1">
                                                        {date !== '-' ? moment(date).format('DD-MM-YYYY') : '-'}
                                                    </td>
                                                    <td className="border border-gray-400 px-2 py-1">{patientName}</td>
                                                    {
                                                        report.invoice_group !== 'Cash' && <td className="border border-gray-400 px-2 py-1">{companyName}</td>
                                                    }
                                                    <td className="border border-gray-400 px-2 py-1 text-right">{charge.toFixed(2)}</td>
                                                    <td className="border border-gray-400 px-2 py-1 text-right">{vat.toFixed(2)}</td>
                                                    <td className="border border-gray-400 px-2 py-1 text-right">{total.toFixed(2)}</td>
                                                    <td className="border border-gray-400 px-2 py-1 text-right">0.00</td>
                                                </tr>
                                            );
                                        })}
                                        <tr className="bg-gray-100 font-semibold">
                                            <td colSpan={report?.invoice_group === 'Cash' ? '3' : '4'} className="border border-gray-400 px-2 py-1 text-left">{t('Sub-Total')}</td>
                                            <td className="border border-gray-400 px-2 py-1 text-right">{subtotal.charge.toFixed(2)}</td>
                                            <td className="border border-gray-400 px-2 py-1 text-right">{subtotal.vat.toFixed(2)}</td>
                                            <td className="border border-gray-400 px-2 py-1 text-right">{subtotal.total.toFixed(2)}</td>
                                            <td className="border border-gray-400 px-2 py-1 text-right">0.00</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                    <div className="mt-8 flex items-start gap-4 w-full">
                        <div className='min-w-[300px]'>
                            <h3 className="text-base font-bold text-gray-800 mb-4 italic">{t('Payment Summary')}</h3>

                            <div className="max-w-md">
                                <table className="w-full border-collapse border border-gray-400 text-xs">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="border border-gray-400 px-2 py-1 text-left font-semibold">{t('Method')}</th>
                                            <th className="border border-gray-400 px-2 py-1 text-right font-semibold">{t('Total Amount')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="border border-gray-400 px-2 py-1">{t('Cash')}</td>
                                            <td className="border border-gray-400 px-2 py-1 text-right">{paymentGrandTotals.cash.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td className="border border-gray-400 px-2 py-1">{t('Card')}</td>
                                            <td className="border border-gray-400 px-2 py-1 text-right">{paymentGrandTotals.card.toFixed(2)}</td>
                                        </tr>
                                        {Object?.entries(cardTypeTotals)?.map(([type, amount]) => (
                                            <tr key={type}>
                                                <td className="border border-gray-400 px-2 py-1 pl-4">└─ {type}</td>
                                                <td className="border border-gray-400 px-2 py-1 text-right">{amount?.toFixed(2)}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td className="border border-gray-400 px-2 py-1">{t('Bank')}</td>
                                            <td className="border border-gray-400 px-2 py-1 text-right">{paymentGrandTotals.bank.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td className="border border-gray-400 px-2 py-1">{t('Deposit')}</td>
                                            <td className="border border-gray-400 px-2 py-1 text-right">{paymentGrandTotals.deposit.toFixed(2)}</td>
                                        </tr>
                                        <tr className="bg-gray-200 font-bold">
                                            <td className="border border-gray-400 px-2 py-1 text-left">{t('Total')}</td>
                                            <td className="border border-gray-400 px-2 py-1 text-right">{totalPaymentReceived.toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className='min-w-[300px]'>
                            <h3 className="text-base font-bold text-gray-800 mb-4 italic">{t('Payers Revenue')}</h3>

                            <div className="max-w-md">
                                <table className="w-full border-collapse border border-gray-400 text-xs">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="border border-gray-400 px-2 py-1 text-left font-semibold">{t('Payer')}</th>
                                            <th className="border border-gray-400 px-2 py-1 text-right font-semibold">{t('Revenue')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object?.entries(payersRevenueTotals)?.map(([payer, revenue]) => (
                                            <tr key={payer}>
                                                <td className="border border-gray-400 px-2 py-1">{payer}</td>
                                                <td className="border border-gray-400 px-2 py-1 text-right">{revenue?.toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!loading && !data && (
                <div className="bg-white p-8 rounded-lg shadow-sm text-center">
                    <p className="text-gray-500">{t('No data available. Please generate a report.')}</p>
                </div>
            )}
            <iframe ref={iframeRef} style={{ display: 'none' }} title="print_frame"></iframe>
        </div>
    );
};

export default DailyInvoiceReport;
